import type React from 'react';
import {useEffect, useMemo} from 'react';
import {useGetGroupedSupportCompanies} from '../../../../../core/repositories/companyRepository';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../../../shared/components/SnackBar';
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from '../../../constants/errorMessages';
import {useProgressAddMgtActions} from '../../../usecase/useProgressAddMgtActions';
import {ProgressMgtCreateDrawer} from '../Presenter/Progress/ProgressMgtCreateDrawer';

type Props = {
  opened: boolean;
  onClose: () => void;
  existingCompanyIds?: number[];
  onBack?: () => void;
  initialStartDate?: string;
  initialEndDate?: string;
};

export const ProgressMgtCreateContainer: React.FC<Props> = ({
  opened,
  onClose,
  existingCompanyIds = [],
  onBack,
  initialStartDate,
  initialEndDate,
}) => {
  const {form, handleSubmit, isLoading, errorMessage, clearMessages} =
    useProgressAddMgtActions(
      existingCompanyIds,
      initialStartDate,
      initialEndDate,
    );

  // モーダルが開いたときに初期値を設定
  useEffect(() => {
    if (opened && (initialStartDate || initialEndDate)) {
      form.setFieldValue('startDate', initialStartDate);
      form.setFieldValue('endDate', initialEndDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initialStartDate, initialEndDate]);

  // 企業データの取得
  const {data: companiesData} = useGetGroupedSupportCompanies({
    queryParams: {},
    config: {
      enabled: opened,
      staleTime: Number.POSITIVE_INFINITY,
      cacheTime: 30 * 60 * 1000,
    },
  });

  // 企業グループをフラット化し、重複を除去してAutocomplete用のデータ形式に変換
  const companyList = useMemo(() => {
    if (!companiesData?.ok) return [];

    const companiesFromApi = companiesData.value.companyGroups.flatMap(
      (group) => group.companies,
    );

    // 重複を除去
    const uniqueCompaniesMap = companiesFromApi.reduce((acc, company) => {
      if (!acc.has(company.id)) {
        acc.set(company.id, company);
      }
      return acc;
    }, new Map<number, {id: number; name: string; isFollowed: boolean}>());

    return Array.from(uniqueCompaniesMap.values()).map((company) => ({
      id: company.id,
      value: company.name,
    }));
  }, [companiesData]);

  const handleClose = () => {
    onClose();
    form.reset();
    clearMessages();
  };

  const handleSubmitForm = async () => {
    try {
      const result = await handleSubmit();
      if (result.success) {
        showSuccessSnackBar(SUCCESS_MESSAGES.ENTRY_MANAGEMENT_SAVED);
        handleClose();
      } else {
        showErrorSnackBar({
          ...ERROR_MESSAGES.ENTRY_MANAGEMENT_SAVE_FAILED,
          message:
            errorMessage || ERROR_MESSAGES.ENTRY_MANAGEMENT_SAVE_FAILED.message,
        });
      }
    } catch (_error) {
      showErrorSnackBar(ERROR_MESSAGES.ENTRY_MANAGEMENT_SAVE_FAILED);
    }
  };

  // 企業名blur時のハンドラー（入力値から企業IDを解決）
  const handleCompanyNameBlur = () => {
    // 入力値に完全一致する企業を検索してIDを決定
    const matchedCompany = companyList.find(
      (company) => company.value === form.values.companyName,
    );
    form.setFieldValue('companyId', matchedCompany?.id ?? 0);
    form.validateField('companyName');
  };

  return (
    <ProgressMgtCreateDrawer
      opened={opened}
      onClose={handleClose}
      onSubmit={handleSubmitForm}
      formData={form.values}
      errors={form.errors}
      onFormChange={(field, value) => form.setFieldValue(field, value)}
      onFieldBlur={(field) => form.validateField(field)}
      isLoading={isLoading}
      companyList={companyList}
      onCompanyNameBlur={handleCompanyNameBlur}
      errorMessage={errorMessage}
      onBack={onBack}
    />
  );
};
